import { useRedirectToPage, useSectionProgress } from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import { useAuth } from "src/context";
import {
  fromAssetsList,
  type AssetFormData,
  type AssetListFormData,
} from "src/api/adapters/assets";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import { useEffect, useReducer, useState } from "react";
import FinancialAccountFormModal from "./FinancialAccountFormModal";
import FinancialAccountItem from "./FinancialAccountItem";
import { account_type } from "src/constants";
import {
  Add01Icon,
  BankIcon,
  FormTable,
  FormTableAddButton,
  useContainerRadiusSync,
  useFormTableAddButtonState,
} from "@porchpass/ui";
import { SkipSection } from "@porchpass/api-client";

const FormattedSubtitle = () => (
  <>
    Rest assured, your information is encrypted & secure.
    <br />
    <br />
    We use these details to gain a comprehensive understanding of your assets,
    which helps us evaluate your application more accurately.
  </>
);

export default () => {
  const api = useWebClientApi();
  const { user: firebaseUser } = useAuth();
  const [focusedFinancialAccount, setFocusedFinancialAccount] =
    useState<AssetFormData>();
  const sectionProgress = useSectionProgress();
  const { formData, reloadFormData } = useFormLoad<AssetListFormData>(
    {
      financialAccounts: [],
    },
    () => api.getAssets(),
    (data) => fromAssetsList(data as Parameters<typeof fromAssetsList>[0]),
    { enabled: !!firebaseUser },
  );

  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  const [skipSectionSubmitHandler, skipLoading] = useDlaFormSubmit(async () => {
    await api.skipSection(SkipSection.Assets);
  });

  const redirectToExpensePage = useRedirectToPage("expenses");
  const redirectToProgressPage = useRedirectToPage("../../progress");

  const closeHandler = () => {
    setFocusedFinancialAccount(undefined);
  };

  const confirmHandler = () => {
    setFocusedFinancialAccount(undefined);
    reloadFormData();
  };

  const clickSubmitHandler = async () => {
    if (formData.financialAccounts.length === 0) {
      await skipSectionSubmitHandler({});
    }

    if (sectionProgress["financial_accounts"] && sectionProgress["expenses"]) {
      redirectToProgressPage();
    } else {
      redirectToExpensePage();
    }
  };

  const otherFinancialAccountsCount = formData.financialAccounts.filter(
    (financialAccount) => financialAccount.account_type === "OTHER",
  ).length;

  const getAvailableAccountTypes = () =>
    account_type.filter(
      (accountType) =>
        (accountType.id === "OTHER" && otherFinancialAccountsCount < 2) ||
        !formData.financialAccounts.find(
          (selectedAccount) => selectedAccount.account_type === accountType.id,
        ),
    );

  const [availableAccountTypeOptions, updateAvailableAccountTypeOptions] =
    useReducer(getAvailableAccountTypes, getAvailableAccountTypes());

  useEffect(() => {
    if (formData.financialAccounts) {
      updateAvailableAccountTypeOptions();
    }
  }, [formData]);

  let footerTitle = "Next";
  if (sectionProgress["financial_accounts"] && sectionProgress["expenses"])
    footerTitle = "Save";
  if (formData.financialAccounts.length === 0) footerTitle = "I don't have any";
  return (
    <FormLayout
      title={
        availableAccountTypeOptions.length > 0
          ? "Add your banking and financial accounts."
          : "The maximum number of accounts has been added."
      }
      subtitle={<FormattedSubtitle />}
      headerTitle="Financial Accounts"
      stepIndex={5}
      footerTitle={footerTitle}
      clickHandler={() => void clickSubmitHandler()}
      isLoading={skipLoading}
    >
      <Col>
        <FormTable
          title="Financial Accounts"
          headerIcon={
            <BankIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
          style={containerStyles.style}
        >
          {formData.financialAccounts.map((financialAccount, index) => (
            <FinancialAccountItem
              key={financialAccount.id}
              index={index}
              financialAccount={financialAccount}
              editHandler={() => setFocusedFinancialAccount(financialAccount)}
              closeHandler={closeHandler}
              deleteHandler={confirmHandler}
            />
          ))}
          {availableAccountTypeOptions.length > 0 && (
            <FormTableAddButton
              type="button"
              onClick={() => setFocusedFinancialAccount({})}
              {...addButtonState.interactionProps}
            >
              <Add01Icon variant="stroke-rounded" size={16} />
              {formData.financialAccounts.length === 0
                ? "Add account"
                : "Additional account"}
            </FormTableAddButton>
          )}
        </FormTable>
      </Col>
      {focusedFinancialAccount && (
        <FinancialAccountFormModal
          key={focusedFinancialAccount.id ?? "new"}
          financialAccount={focusedFinancialAccount}
          availableAccountOptions={availableAccountTypeOptions}
          closeHandler={closeHandler}
          confirmHandler={confirmHandler}
        />
      )}
    </FormLayout>
  );
};
